import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout, { RouteGuard } from "@/components/Layout";
import { LocaleProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import UserControlDashboard from "@/pages/UserControlDashboard";
import KpiPortal from "@/pages/KpiPortal";
import { CustomersPage, CustomerDetailPage, SuppliersPage } from "@/pages/MasterData";
import { QuarantinePage } from "@/pages/Quarantine";
import { GMPPage } from "@/pages/GMP";
import TrackingPage from "@/pages/Tracking";
import { FormulasPage } from "@/pages/Formulas";
import { InventoryPage } from "@/pages/Inventory";
import { SalesOrdersPage, SalesOrderDetailPage } from "@/pages/SalesOrders";
import { ProductionOrdersPage, ProductionDetailPage } from "@/pages/Production";
import { QualityPage, TraceabilityPage } from "@/pages/Quality";
import { FinishedProductsPage } from "@/pages/FinishedProducts";
import { PurchasingPage, ReportsPage, SettingsPage, DocumentsPage } from "@/pages/System";
import { UsersPage } from "@/pages/Users";
import { RDPortalPage } from "@/pages/RD";
import { FinancePage, RequestsPage } from "@/pages/Muhasebe";
import TraceabilitySearch from "@/pages/TraceabilitySearch";
import RecallsPage from "@/pages/Recalls";
import CustomerServicePage from "@/pages/CustomerService";
import { QuotationsPage } from "@/pages/Quotations";
import ProformaPage from "@/pages/Proforma";
import CatalogPage from "@/pages/Catalog";
import { MeetingsPage, MeetingDetailPage } from "@/pages/Meetings";
import PersonnelPage from "@/pages/Personnel";
import SupportsPage from "@/pages/Supports";
import TrainingsPage from "@/pages/Trainings";
import IntegrationsPage from "@/pages/Integrations";
import RegulatoryPage from "@/pages/Regulatory";
import MaterialsHubPage from "@/pages/MaterialsHub";
import MonthlyReportsPage from "@/pages/MonthlyReports";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";
import AutoTooltip from "@/components/AutoTooltip";
import ErrorBoundary from "@/components/ErrorBoundary";

function Protected({ children, moduleKey }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{moduleKey ? <RouteGuard moduleKey={moduleKey}>{children}</RouteGuard> : children}</Layout>;
}

function App() {
  return (
    <AuthProvider>
      <LocaleProvider>
      <ThemeProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected moduleKey="dashboard"><Dashboard /></Protected>} />
          <Route path="/user-control-dashboard" element={<Protected moduleKey="user_control_dashboard"><UserControlDashboard /></Protected>} />
          {/* iter326 — eski /user-dashboard URL'si kalıcı olarak yeni panele yönlendirilir. */}
          <Route path="/user-dashboard" element={<Navigate to="/user-control-dashboard" replace />} />
          <Route path="/kpi-portal" element={<Protected moduleKey="kpi_portal"><KpiPortal /></Protected>} />
          <Route path="/customer-service" element={<Protected moduleKey="customer_service"><CustomerServicePage /></Protected>} />
          <Route path="/meetings" element={<Protected moduleKey="meetings"><MeetingsPage /></Protected>} />
          <Route path="/meetings/:id" element={<Protected moduleKey="meetings"><MeetingDetailPage /></Protected>} />
          <Route path="/customers" element={<Protected moduleKey="customers"><CustomersPage /></Protected>} />
          <Route path="/customers/:id" element={<Protected moduleKey="customers"><CustomerDetailPage /></Protected>} />
          <Route path="/suppliers" element={<Protected moduleKey="suppliers"><SuppliersPage /></Protected>} />
          <Route path="/materials" element={<Navigate to="/inventory" replace />} />
          <Route path="/formulas" element={<Protected moduleKey="formulas"><FormulasPage /></Protected>} />
          <Route path="/warehouses" element={<Navigate to="/inventory" replace />} />
          <Route path="/inventory" element={<Protected moduleKey="inventory"><InventoryPage /></Protected>} />
          <Route path="/materials-hub" element={<Protected moduleKey="inventory"><MaterialsHubPage /></Protected>} />
          <Route path="/purchasing" element={<Protected moduleKey="purchasing"><PurchasingPage /></Protected>} />
          <Route path="/sales-orders" element={<Protected moduleKey="sales_orders"><SalesOrdersPage /></Protected>} />
          <Route path="/sales-orders/:id" element={<Protected moduleKey="sales_orders"><SalesOrderDetailPage /></Protected>} />
          <Route path="/production" element={<Protected moduleKey="production"><ProductionOrdersPage /></Protected>} />
          <Route path="/production/:id" element={<Protected moduleKey="production"><ProductionDetailPage /></Protected>} />
          <Route path="/quality" element={<Protected moduleKey="quality"><QualityPage /></Protected>} />
          <Route path="/quarantine" element={<Protected moduleKey="quarantine"><QuarantinePage /></Protected>} />
          <Route path="/gmp" element={<Protected moduleKey="gmp"><GMPPage /></Protected>} />
          <Route path="/finished-products" element={<Protected moduleKey="finished_products"><FinishedProductsPage /></Protected>} />
          <Route path="/shipments" element={<Navigate to="/finished-products" replace />} />
          <Route path="/traceability" element={<Protected moduleKey="traceability"><TraceabilitySearch /></Protected>} />
          <Route path="/personnel" element={<Protected moduleKey="personnel"><PersonnelPage /></Protected>} />
          <Route path="/supports" element={<Protected><SupportsPage /></Protected>} />
          <Route path="/trainings" element={<Protected moduleKey="personnel"><TrainingsPage /></Protected>} />
          <Route path="/recalls" element={<Protected moduleKey="recalls"><RecallsPage /></Protected>} />
          <Route path="/traceability-legacy" element={<Protected moduleKey="traceability"><TraceabilityPage /></Protected>} />
          <Route path="/track/:lotId" element={<Protected><TrackingPage /></Protected>} />
          <Route path="/reports" element={<Protected moduleKey="reports"><ReportsPage /></Protected>} />
          <Route path="/notifications" element={<Navigate to="/requests" replace />} />
          <Route path="/documents" element={<Protected><DocumentsPage /></Protected>} />
          <Route path="/rd" element={<Protected moduleKey="rd"><RDPortalPage /></Protected>} />
          <Route path="/quotations" element={<Protected moduleKey="quotations"><QuotationsPage /></Protected>} />
          <Route path="/proformas" element={<Protected moduleKey="proforma"><ProformaPage /></Protected>} />
          <Route path="/catalog" element={<Protected><CatalogPage /></Protected>} />
          <Route path="/finance" element={<Protected moduleKey="finance"><FinancePage /></Protected>} />
          <Route path="/monthly-reports" element={<Protected moduleKey="finance"><MonthlyReportsPage /></Protected>} />
          <Route path="/requests" element={<Protected moduleKey="requests"><RequestsPage /></Protected>} />
          <Route path="/users" element={<Protected moduleKey="users"><UsersPage /></Protected>} />
          <Route path="/integrations" element={<Protected moduleKey="integrations"><IntegrationsPage /></Protected>} />
          <Route path="/regulatory" element={<Protected moduleKey="regulatory"><RegulatoryPage /></Protected>} />
          <Route path="/settings" element={<Protected moduleKey="settings"><SettingsPage /></Protected>} />
          {/* iter239 (F14) — Wildcard fallback so invalid URLs land on the
              Dashboard instead of a broken/empty page. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
        <PdfPreviewDialog />
        <AutoTooltip />
      </BrowserRouter>
      </ThemeProvider>
      </LocaleProvider>
    </AuthProvider>
  );
}
export default App;
